import {getSiteVar} from "../services/site-vars";

const CACHE_STORAGE_KEYS = {
    freshNonce: 'wl_fresh_nonce_v1',
};
const CACHE_STORAGE_VERSION = 1;
const DEFAULT_FRESH_NONCE_CACHE_TTL_MS = 5 * 60 * 1000;
const CACHE_EARLY_REFRESH_SKEW_MS = 30 * 1000;
let resolvedCacheStorage;

const parsePositiveInt = (value, fallback) => {
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};
const get_fresh_nonce_cache_ttl_ms = () => parsePositiveInt(getSiteVar('fresh_nonce_cache_ttl_ms'), DEFAULT_FRESH_NONCE_CACHE_TTL_MS);
const get_cache_context = () => ({
    userId: getSiteVar('current_user_id') !== undefined && getSiteVar('current_user_id') !== null ? String(getSiteVar('current_user_id')) : null,
    role: getSiteVar('current_user_role') ? String(getSiteVar('current_user_role')) : null,
});
const normalize_cache_context = (context = {}) => ({
    userId: context.userId !== undefined && context.userId !== null ? String(context.userId) : null,
    role: context.role ? String(context.role) : null,
});
const is_cache_context_match = (storedContext, currentContext) => {
    const stored = normalize_cache_context(storedContext || {});
    const current = normalize_cache_context(currentContext || {});
    return stored.userId === current.userId && stored.role === current.role;
};
const get_cache_storage = () => {
    if (resolvedCacheStorage !== undefined) {
        return resolvedCacheStorage;
    }

    const storageCandidates = [];
    if (typeof window !== 'undefined') {
        try {
            if (window.localStorage) storageCandidates.push(window.localStorage);
        } catch (e) {
            // localStorage can throw in restricted contexts
        }
        try {
            if (window.sessionStorage) storageCandidates.push(window.sessionStorage);
        } catch (e) {
            // sessionStorage can throw in restricted contexts
        }
    }

    const testKey = '__wl_cache_storage_probe__';
    for (const storage of storageCandidates) {
        try {
            storage.setItem(testKey, '1');
            storage.removeItem(testKey);
            resolvedCacheStorage = storage;
            return resolvedCacheStorage;
        } catch (e) {
            // continue to next storage candidate
        }
    }

    resolvedCacheStorage = null;
    return resolvedCacheStorage;
};
const cache_storage_get = (key) => {
    const storage = get_cache_storage();
    if (!storage) return null;
    try {
        return storage.getItem(key);
    } catch (e) {
        return null;
    }
};
const cache_storage_set = (key, value) => {
    const storage = get_cache_storage();
    if (!storage) return;
    try {
        storage.setItem(key, value);
    } catch (e) {
        // Ignore storage failures and fallback to in-memory cache
    }
};
const cache_storage_remove = (key) => {
    const storage = get_cache_storage();
    if (!storage) return;
    try {
        storage.removeItem(key);
    } catch (e) {
        // Ignore storage failures
    }
};
const read_persistent_cache_data = (storageKey) => {
    const rawValue = cache_storage_get(storageKey);
    if (!rawValue) {
        return null;
    }

    let envelope;
    try {
        envelope = JSON.parse(rawValue);
    } catch (e) {
        cache_storage_remove(storageKey);
        return null;
    }

    if (!envelope || typeof envelope !== 'object' || envelope.version !== CACHE_STORAGE_VERSION) {
        cache_storage_remove(storageKey);
        return null;
    }

    if (!is_cache_context_match(envelope.context, get_cache_context())) {
        cache_storage_remove(storageKey);
        return null;
    }

    const expiresAtMs = parsePositiveInt(envelope.expiresAtMs, 0);
    const refreshThresholdMs = Math.max(0, expiresAtMs - CACHE_EARLY_REFRESH_SKEW_MS);
    if (!expiresAtMs || Date.now() >= refreshThresholdMs) {
        cache_storage_remove(storageKey);
        return null;
    }

    return envelope.data;
};
const write_persistent_cache_data = (storageKey, data, ttlMs) => {
    const safeTtlMs = parsePositiveInt(ttlMs, 0);
    if (!safeTtlMs) {
        return;
    }

    const fetchedAtMs = Date.now();
    const envelope = {
        version: CACHE_STORAGE_VERSION,
        context: normalize_cache_context(get_cache_context()),
        fetchedAtMs: fetchedAtMs,
        expiresAtMs: fetchedAtMs + safeTtlMs,
        data: data,
    };

    cache_storage_set(storageKey, JSON.stringify(envelope));
};
const clear_persistent_cache_data = (storageKey) => {
    cache_storage_remove(storageKey);
};

export {
    CACHE_STORAGE_KEYS,
    get_fresh_nonce_cache_ttl_ms,
    read_persistent_cache_data,
    write_persistent_cache_data,
    clear_persistent_cache_data,
};
